import { useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { AUTH_SOCIAL_DIVIDER_LABEL } from 'common/constants/auth';
import { Box, Button, Checkbox, Stack, Text, TextLink } from 'components/ui';
import { AuthDivider, AuthPasswordField } from 'pages/Auth/components';
import { useAuthInitialFocus } from 'pages/Auth/hooks';
import {
  SIGN_IN_FORGOT_PASSWORD_LABEL,
  SIGN_IN_PASSWORD_LABEL,
  SIGN_IN_PASSWORD_PLACEHOLDER,
  SIGN_IN_PASSWORD_SUBMIT_LABEL,
  SIGN_IN_REMEMBER_ME_LABEL,
  SIGN_IN_SSO_PASSWORD_ALTERNATIVE_LABEL,
} from 'pages/Auth/SignIn/SignIn.constants';
import { SIGN_IN_TEST_IDS } from 'pages/Auth/SignIn/SignIn.testIds';
import { isSignInPasswordReady } from 'pages/Auth/SignIn/SignIn.utils';
import { getVisibleFieldErrorMessage } from 'pages/Auth/utils';
import type { SignInPasswordStepProps } from './SignInPasswordStep.types';

const SignInPasswordStep = ({
  form,
  isSubmitting,
  onRememberSignInChange,
  onSubmit,
  onUseSso,
  passwordErrorMessageOverride,
  rememberSignIn,
  resetPasswordHref,
  showSsoFallback,
}: SignInPasswordStepProps) => {
  const focusPasswordField = useCallback(() => {
    form.setFocus('password');
  }, [form]);

  useAuthInitialFocus(focusPasswordField, !isSubmitting);
  const watchedPassword = form.watch('password');
  const isSubmitDisabled = isSubmitting || !isSignInPasswordReady(watchedPassword);
  const passwordHelperText = passwordErrorMessageOverride
    ?? getVisibleFieldErrorMessage({
      error: form.formState.errors.password,
      hiddenErrorTypes: ['server'],
    });

  return (
    <Stack component="form" noValidate onSubmit={onSubmit} spacing={2}>
      <AuthPasswordField
        autoComplete="current-password"
        disabled={isSubmitting}
        error={Boolean(passwordErrorMessageOverride) || Boolean(form.formState.errors.password)}
        helperText={passwordHelperText}
        id="auth-password-input"
        label={SIGN_IN_PASSWORD_LABEL}
        placeholder={SIGN_IN_PASSWORD_PLACEHOLDER}
        {...form.register('password')}
      />
      <Box alignItems="center" display="flex" flexWrap="wrap" gap={1} justifyContent="space-between" rowGap={0.5}>
        <Checkbox
          checked={rememberSignIn}
          disabled={isSubmitting}
          id="auth-password-remember"
          label={
            <Text component="span" variant="body2" visualVariant="inlineEmphasis">
              {SIGN_IN_REMEMBER_ME_LABEL}
            </Text>
          }
          onChange={onRememberSignInChange}
        />
        <TextLink<typeof RouterLink>
          component={RouterLink}
          disabled={isSubmitting}
          to={resetPasswordHref}
          variant="body2"
          visualVariant="anchor"
        >
          {SIGN_IN_FORGOT_PASSWORD_LABEL}
        </TextLink>
      </Box>
      <Button
        data-testid={SIGN_IN_TEST_IDS.passwordSubmitButton}
        disabled={isSubmitDisabled}
        fullWidth
        loading={isSubmitting}
        loadingBehavior="inline"
        type="submit"
        variant="contained"
        visualVariant="primary"
      >
        {SIGN_IN_PASSWORD_SUBMIT_LABEL}
      </Button>
      {showSsoFallback ? (
        <>
          <AuthDivider label={AUTH_SOCIAL_DIVIDER_LABEL} />
          <Button
            disabled={isSubmitting}
            fullWidth
            onClick={onUseSso}
            type="button"
            variant="outlined"
            visualVariant="primaryOutline"
          >
            {SIGN_IN_SSO_PASSWORD_ALTERNATIVE_LABEL}
          </Button>
        </>
      ) : null}
    </Stack>
  );
};

export default SignInPasswordStep;
